import { useState } from 'react'
import { TextInput } from '../../../components/FormUtils/TextInput'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { ErrorText } from '../../../components/FormUtils/ErrorText'
import FbButton from '../../../components/FbUI/FbButton'
import { FbLink } from 'components/FbUI/FbLink'
import { getClient } from '../../../utils/http-client'

const ssoLoginFormShape = z.object({
  email: z.string().email('Please enter a valid email address'),
  non_field_errors: z.string().optional(),
})

function SSOLogin() {
  const [isChecking, setIsChecking] = useState(false)

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
    setError,
  } = useForm({
    defaultValues: {
      email: '',
      non_field_errors: '',
    },
    resolver: zodResolver(ssoLoginFormShape),
  })

  const onSubmit = handleSubmit(
    async (values) => {
      setIsChecking(true)
      const client = getClient({ reloadOnAuthError: false })

      try {
        // Check if SSO is required for this email
        const checkResponse = await client.post('auth/sso/check/', {
          email: values.email,
        })

        const { data } = checkResponse

        // Handle different SSO check responses
        if (data.error) {
          // Display specific error from backend
          setError('non_field_errors', {
            type: 'manual',
            message: data.error,
          })
        } else if (data.sso_configured && data.sso_url) {
          // SSO is configured - redirect to SSO regardless of whether it's required
          window.location.href = data.sso_url
        } else if (!data.sso_configured) {
          // SSO not configured - show error with support contact
          setError('non_field_errors', {
            type: 'manual',
            message:
              data.error ||
              'SSO is not configured for your organization. Please contact support@firstbite.io.',
          })
        } else {
          // Fallback for unexpected response
          setError('non_field_errors', {
            type: 'manual',
            message:
              'Unable to process SSO request. Please try again or use regular login.',
          })
        }
      } catch (error: any) {
        console.error('SSO check failed:', error)

        if (error.response?.data?.error) {
          setError('non_field_errors', {
            type: 'manual',
            message: error.response.data.error,
          })
        } else {
          setError('non_field_errors', {
            type: 'manual',
            message: 'Failed to check SSO status. Please try again.',
          })
        }
      } finally {
        setIsChecking(false)
      }
    },
    (errors) => console.warn(errors)
  )

  return (
    <form onSubmit={onSubmit} className="w-96 flex-shrink-0 max-[400px]:w-full">
      <h1 className="text-2xl mb-2 text-center">Sign in with SSO</h1>
      <p className="text-gray-600 mb-8 text-center text-sm">
        Enter your work email address to sign in with your company&apos;s Single
        Sign-On
      </p>

      <TextInput
        control={control}
        name="email"
        label="Work Email Address"
        placeholder="you@company.com"
      />

      <div className="mt-6 flex justify-center">
        <FbButton
          type="submit"
          loading={isSubmitting || isChecking}
          disabled={isSubmitting || isChecking}
          className="w-full"
        >
          {isChecking ? 'Checking...' : 'Continue with SSO'}
        </FbButton>
      </div>

      <ErrorText control={control} name={'non_field_errors'} />

      <div className="flex items-center my-8">
        <div className="flex-1 h-px bg-gray-300"></div>
        <span className="px-4 text-gray-600 text-sm">or</span>
        <div className="flex-1 h-px bg-gray-300"></div>
      </div>

      <div className="text-center mb-6">
        <FbLink to="/login">Sign in with email and password</FbLink>
      </div>

      <div className="flex justify-center items-center gap-2 pt-6 border-t border-gray-300">
        <span className="text-gray-600 text-sm">
          Don&apos;t have an account?
        </span>
        <FbLink to="/signup">Sign up</FbLink>
      </div>
    </form>
  )
}

export default SSOLogin
